/**
 * Словарь вердиктов, общий для сервера и интерфейса.
 *
 * Порядок в VERDICT_SEVERITY — не украшение: по нему считается сводный
 * вердикт объекта и сортировка на главном экране. Худшее должно всплывать
 * наверх само, иначе владелец увидит «в основном всё хорошо» и не заметит
 * машину, которая не бэкапится вовсе.
 */

export const VERDICTS = ['ok', 'warn', 'stale', 'failed', 'missing', 'unknown'] as const;
export type Verdict = (typeof VERDICTS)[number];

/** Чем больше, тем хуже. unknown стоит между warn и stale намеренно: */
/** «мы не знаем» тревожнее лёгкого предупреждения, но не хуже пропажи копий. */
/**
 * Единственный порядок вердиктов в приложении.
 *
 * Одно время их было два — этот и VERDICT_ORDER, скопированный в обработчик
 * сводки, — и они разошлись ровно там, где комментарий выше это и предсказывал:
 * unknown стоял то выше warn, то ниже. Одна и та же картина получала разные
 * вердикты в матрице и в топологии, и понять, какому верить, было нельзя.
 */
export const VERDICT_SEVERITY: Record<Verdict, number> = {
  ok: 0,
  warn: 1,
  unknown: 2,
  stale: 3,
  failed: 4,
  missing: 5,
};

export const VERDICT_LABELS: Record<Verdict, string> = {
  ok: 'в норме',
  warn: 'внимание',
  unknown: 'нет данных',
  stale: 'устарел',
  failed: 'сбой',
  missing: 'нет копий',
};

/**
 * Уровни защиты — лестница от железа к приложению.
 *
 * Порядок не алфавитный и не исторический: он повторяет то, как одно лежит
 * внутри другого. Гипервизор снимает образ машины; внутри машины снимается
 * её файловая система; внутри неё работает контейнеризатор со своими томами;
 * внутри контейнеров — базы, медиафайлы и само приложение.
 *
 * Из порядка следует смысл каждой клетки. Копия на верхней ступени защищает
 * всё, что ниже, но восстанавливается дороже: поднять образ машины ради
 * одной таблицы можно, но это другая по стоимости операция, чем взять дамп.
 * Поэтому «зелено сверху» не отменяет вопроса «а что внизу».
 *
 * `config` стоит особняком и последним. Это не ступень той же лестницы, а
 * отдельный слой: конфигурация устройства — маршрутизатора, межсетевого
 * экрана. У неё нет ни машины, ни файловой системы в нашем распоряжении, и
 * образ виртуалки её не заменяет: восстановить правила из образа можно, а
 * из физической коробки — неоткуда.
 */
export const LEVELS = [
  'hypervisor', 'os', 'container', 'database', 'data', 'application', 'config',
] as const;
export type LevelName = (typeof LEVELS)[number];

export const LEVEL_LABELS: Record<LevelName, string> = {
  hypervisor: 'Образ машины',
  os: 'Файлы машины',
  container: 'Контейнеры и тома',
  database: 'База данных',
  data: 'Медиа и данные',
  application: 'Приложение',
  config: 'Конфигурация устройства',
};

export const LEVEL_SHORT: Record<LevelName, string> = {
  hypervisor: 'ОБРАЗ',
  os: 'ФАЙЛЫ',
  container: 'КОНТ',
  database: 'БД',
  data: 'ДАННЫЕ',
  application: 'ПРИЛ',
  config: 'КОНФ',
};

/**
 * Что уровень означает словами — для подсказки к заголовку колонки.
 *
 * Короткая подпись экономит место, но «ФАЙЛЫ» и «ДАННЫЕ» рядом друг с другом
 * без пояснения одинаково загадочны, а разница между ними решающая: первое
 * снимается изнутри машины целиком, второе — это медиатека, которую ничем,
 * кроме неё самой, не восстановить.
 */
export const LEVEL_HINTS: Record<LevelName, string> = {
  hypervisor: 'Снимок всей машины со стороны гипервизора. Защищает всё внутри, но восстанавливается дороже всего.',
  os: 'Копия файловой системы изнутри машины — агентом или средствами ОС.',
  container: 'Образы и тома контейнеризатора: то, что переживёт пересборку стека.',
  database: 'Дамп базы. Образ машины его не заменяет: из образа база поднимается в состоянии на момент снимка, без согласованности.',
  data: 'Медиафайлы и данные приложения. Восстановить их неоткуда, кроме как из копии: это загруженные картинки, вложения, документы.',
  application: 'Собственная выгрузка приложения — то, что оно умеет отдать само.',
  config: 'Конфигурация устройства: правила маршрутизатора, межсетевого экрана. Отдельный слой, не ступень лестницы выше.',
};

export type LevelPolicy = 'expected' | 'not-needed' | 'undecided';

export function worst(verdicts: Verdict[]): Verdict {
  if (verdicts.length === 0) return 'unknown';
  return verdicts.reduce((a, b) => (VERDICT_SEVERITY[b] > VERDICT_SEVERITY[a] ? b : a));
}

/**
 * Вердикт по возрасту копии. Отсутствие копий и отсутствие данных о них —
 * разные вещи: первое тревога, второе повод посмотреть, почему сборщик молчит.
 */
export function freshnessVerdict(input: {
  lastAt: Date | string | null;
  warnHours: number;
  maxHours: number;
  now?: Date;
}): { verdict: Verdict; reason: string; ageHours: number | null } {
  if (!input.lastAt) {
    return { verdict: 'missing', reason: 'копий не найдено', ageHours: null };
  }
  const last = typeof input.lastAt === 'string' ? new Date(input.lastAt) : input.lastAt;
  const now = input.now ?? new Date();
  const ageHours = (now.getTime() - last.getTime()) / 3_600_000;

  if (ageHours > input.maxHours) {
    return {
      verdict: 'stale',
      reason: `последняя копия ${Math.round(ageHours)} ч назад, порог ${input.maxHours} ч`,
      ageHours,
    };
  }
  if (ageHours > input.warnHours) {
    return {
      verdict: 'warn',
      reason: `последняя копия ${Math.round(ageHours)} ч назад`,
      ageHours,
    };
  }
  return { verdict: 'ok', reason: `последняя копия ${Math.round(ageHours)} ч назад`, ageHours };
}

export function formatAge(hours: number | null): string {
  if (hours === null) return '—';
  if (hours < 1) return `${Math.round(hours * 60)} мин`;
  if (hours < 48) return `${Math.round(hours)} ч`;
  return `${Math.round(hours / 24)} дн`;
}

/** Уровень события в ленте. Отдельно от вердиктов: событие говорит о
 *  происшествии, вердикт — о состоянии. */
export type EventLevel = 'info' | 'warn' | 'error';


/**
 * Считается ли это проблемой — одно правило на всё приложение.
 *
 * Их было два и они расходились: сводка на сервере считала `unknown`
 * проблемой, экран — нет, и число в письме не сходилось с числом на экране на
 * каждом объекте со слепой зоной. Владелец, сверяющий одно с другим, не мог
 * понять, какому верить.
 *
 * `unknown` СЧИТАЕТСЯ проблемой намеренно. «Мы не знаем» тревожнее лёгкого
 * предупреждения — так же, как в VERDICT_SEVERITY, — потому что за незнанием
 * может стоять что угодно, включая отсутствие копий. Молчать о нём значит
 * записать слепую зону в норму.
 *
 * `lifecycle` не считается: выведенное из эксплуатации и ещё не существующее
 * — это решения, уже принятые человеком, и напоминать о них каждый день
 * значит спорить с ним, а заодно топить в этом споре те строки, ради которых
 * экран и открывают.
 */
export function countsAsProblem(
  verdict: Verdict,
  lifecycle?: 'planned' | 'retired' | undefined,
): boolean {
  if (lifecycle) return false;
  return verdict !== 'ok';
}
